// Call plugin.
import { getCallByWrtcsSessionId } from '../../interfaceNew/selectors'

// Other plugins.
import { getDomain } from '../../../auth/interface/selectors'
import { logManager } from '../../../logs'

// Libraries.
import { select } from 'redux-saga/effects'

const log = logManager.getLogger('CALL')

/**
 * Gets the remote participant data from a notification object's `callNotificationParams` property if it exists.
 * @param {Object} notification The notification object to extract remote participant data from.
 * @returns {Object} Object containing `remoteName` & `remoteNumber` properties.
 *                   Empty object if `callNotificationParams` property does not exist.
 */
export function * getRemoteParticipant (notification) {
  const wrtcsSessionId = notification.sessionParams.sessionData
  const targetCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)

  // If the call isn't found, just log a warning and return an empty object.
  if (!targetCall) {
    log.info('A remote operation is being requested on a call that no longer exists.')
    return {}
  }

  let remoteInfo = {}
  if (!notification.callNotificationParams) {
    // `sessionComplete` notifications don't have `callNotificationParams`.
    log.debug("Notification does not contain property 'callNotificationParams'.")
  } else {
    // A "conference" call is a joined call. Joined calls are _always_ incoming
    //    calls, even if the original call was outgoing.
    const isConference =
      notification.callNotificationParams.callerDisplayNumber &&
      notification.callNotificationParams.callerDisplayNumber.startsWith('conference@')

    const isIncoming = targetCall.direction === 'incoming' || isConference
    /**
     * SPiDR notifications seem to have different "remote participant" properties
     *    in different scenarios. Determine which scenario the call is in to know
     *    how to parse the notification.
     */
    if (isIncoming) {
      remoteInfo = {
        remoteName: notification.callNotificationParams.callerName,
        remoteNumber: notification.callNotificationParams.callerDisplayNumber
      }
    } else {
      remoteInfo = {
        remoteName: notification.callNotificationParams.remoteName,
        remoteNumber: notification.callNotificationParams.remoteDisplayNumber
      }
    }
  }

  /*
   * Sometimes the "display number" doesn't include a domain. Make sure it has
   *    a domain so that it is always consistent.
   * TODO: Figure out in which cases this was needed. Transfer?
   *    Ref: KAA-746
   */
  if (remoteInfo.remoteNumber && !remoteInfo.remoteNumber.includes('@')) {
    const domain = yield select(getDomain)
    remoteInfo.remoteNumber += domain
  }

  // If the data from the notification is undefined, use whatever the existing
  //    value on the call was.
  return {
    remoteName: remoteInfo.remoteName || targetCall.remoteParticipant.displayName,
    remoteNumber: remoteInfo.remoteNumber || targetCall.remoteParticipant.displayNumber
  }
}
